import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Pagination from '../../components/Pagination/Pagination';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import './BuyLeadsPage.css';

const BuyLeadsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { hasPermission, planDisplayName, getFeature, loading: subscriptionLoading } = useSubscription();
  const isAdminUser = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
  const canViewContacts = isAdminUser || (!subscriptionLoading && hasPermission('canViewBuyerContacts'));
  const contactRegion = getFeature('buyerContactRegion');

  const [selectedKeyword, setSelectedKeyword] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // API data
  const [sponsoredLeads, setSponsoredLeads] = useState([]);
  const [regularLeads, setRegularLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);

  // Buy leads view tracking
  const [viewStatus, setViewStatus] = useState({ viewsUsed: 0, viewsLimit: 0, viewedIds: [], loggedIn: false });
  const [revealedLeads, setRevealedLeads] = useState({}); // { leadId: { email, phoneNumber, details, fullName } }
  const [showingLead, setShowingLead] = useState(null); // leadId currently loading

  const dateOptions = ['Today', 'Last 7 Days', 'Last 30 Days', 'All Time'];
  const filterRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowCountryDropdown(false);
        setShowCategoryDropdown(false);
        setShowDateDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getAuthHeaders = () => {
    const authToken = token || localStorage.getItem('token');
    return authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
  };

  // Fetch view status on mount
  useEffect(() => {
    const fetchViewStatus = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/buy-leads/view-status`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setViewStatus(data);
        }
      } catch (error) {
        console.error('Error fetching view status:', error);
      }
    };
    fetchViewStatus();
  }, [user]);

  // Fetch filters on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [countriesRes, categoriesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/buy-leads/filters/countries`),
          fetch(`${API_BASE_URL}/buy-leads/filters/categories`)
        ]);
        if (countriesRes.ok) {
          const data = await countriesRes.json();
          setCountries(data.length > 0 ? data : ['Saudi Arabia', 'United Arab Emirates', 'Qatar', 'Kuwait', 'Bahrain', 'Oman']);
        }
        if (categoriesRes.ok) {
          const data = await categoriesRes.json();
          setCategories(data.length > 0 ? data : ['Building Materials', 'Construction Equipment', 'Steel & Metals', 'Electrical', 'Plumbing']);
        }
      } catch (error) {
        console.error('Error fetching filters:', error);
        setCountries(['Saudi Arabia', 'United Arab Emirates', 'Qatar', 'Kuwait', 'Bahrain', 'Oman']);
        setCategories(['Building Materials', 'Construction Equipment', 'Steel & Metals', 'Electrical', 'Plumbing']);
      }
    };
    fetchFilters();
  }, []);

  // Fetch leads
  const fetchLeads = useCallback(async (page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedKeyword) params.append('keyword', selectedKeyword);
      if (selectedCountry) params.append('country', selectedCountry);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedDate && selectedDate !== 'All Time') params.append('date', selectedDate);
      params.append('limit', ITEMS_PER_PAGE);
      params.append('offset', (page - 1) * ITEMS_PER_PAGE);

      const res = await fetch(`${API_BASE_URL}/buy-leads?${params.toString()}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSponsoredLeads(data.sponsoredLeads || []);
        setRegularLeads(data.regularLeads || []);
        setTotalLeads(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching buy leads:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedKeyword, selectedCountry, selectedCategory, selectedDate]);

  // Fetch on mount and page change
  useEffect(() => {
    fetchLeads(currentPage);
  }, [currentPage, fetchLeads]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchLeads(1);
  };

  // Handle "Show" button click
  const handleShowLead = async (leadId) => {
    if (!user) {
      navigate('/membership-packages');
      return;
    }

    // Already revealed
    if (revealedLeads[leadId]) return;

    setShowingLead(leadId);
    try {
      const res = await fetch(`${API_BASE_URL}/buy-leads/${leadId}/view`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setRevealedLeads(prev => ({ ...prev, [leadId]: data.lead }));
        setViewStatus(prev => ({
          ...prev,
          viewsUsed: data.viewsUsed,
          viewsLimit: data.viewsLimit,
          viewedIds: [...(prev.viewedIds || []), leadId]
        }));
      } else if (data.error === 'upgrade_required') {
        alert('Upgrade your plan to view buy leads.\n\nGCC Plan: 50 views\nGlobal Plan: 100 views');
        navigate('/membership-packages');
      } else if (data.error === 'limit_reached') {
        alert(`You have used all ${data.viewsLimit} buy lead views.\n\nUpgrade your plan for more views.`);
        navigate('/membership-packages');
      } else {
        alert(data.message || 'Failed to view lead details');
      }
    } catch (error) {
      console.error('Error viewing lead:', error);
      alert('Failed to view lead details. Please try again.');
    } finally {
      setShowingLead(null);
    }
  };

  const getDisplayName = (lead) => lead.fullName || lead.companyName;

  const CaretDownIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.031 9.53104L12.531 17.031C12.4614 17.1008 12.3787 17.1561 12.2876 17.1938C12.1966 17.2316 12.099 17.251 12.0004 17.251C11.9019 17.251 11.8043 17.2316 11.7132 17.1938C11.6222 17.1561 11.5394 17.1008 11.4698 17.031L3.96979 9.53104C3.82906 9.39031 3.75 9.19944 3.75 9.00042C3.75 8.80139 3.82906 8.61052 3.96979 8.46979C4.11052 8.32906 4.30139 8.25 4.50042 8.25C4.69944 8.25 4.89031 8.32906 5.03104 8.46979L12.0004 15.4401L18.9698 8.46979C19.0395 8.40011 19.1222 8.34483 19.2132 8.30712C19.3043 8.26941 19.4019 8.25 19.5004 8.25C19.599 8.25 19.6965 8.26941 19.7876 8.30712C19.8786 8.34483 19.9614 8.40011 20.031 8.46979C20.1007 8.53947 20.156 8.6222 20.1937 8.71324C20.2314 8.80429 20.2508 8.90187 20.2508 9.00042C20.2508 9.09896 20.2314 9.19654 20.1937 9.28759C20.156 9.37863 20.1007 9.46136 20.031 9.53104Z" fill="#6D6D6D"/>
    </svg>
  );

  const SearchIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 21L15 15M3 10C3 10.9193 3.18106 11.8295 3.53284 12.6788C3.88463 13.5281 4.40024 14.2997 5.05025 14.9497C5.70026 15.5998 6.47194 16.1154 7.32122 16.4672C8.1705 16.8189 9.08075 17 10 17C10.9193 17 11.8295 16.8189 12.6788 16.4672C13.5281 16.1154 14.2997 15.5998 14.9497 14.9497C15.5998 14.2997 16.1154 13.5281 16.4672 12.6788C16.8189 11.8295 17 10.9193 17 10C17 9.08075 16.8189 8.1705 16.4672 7.32122C16.1154 6.47194 15.5998 5.70026 14.9497 5.05025C14.2997 4.40024 13.5281 3.88463 12.6788 3.53284C11.8295 3.18106 10.9193 3 10 3C9.08075 3 8.1705 3.18106 7.32122 3.53284C6.47194 3.88463 5.70026 4.40024 5.05025 5.05025C4.40024 5.70026 3.88463 6.47194 3.53284 7.32122C3.18106 8.1705 3 9.08075 3 10Z" stroke="white" strokeOpacity="0.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const CheckboxIcon = () => (
    <svg width="24" height="36" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 9H5C3.89543 9 3 9.89543 3 11V25C3 26.1046 3.89543 27 5 27H19C20.1046 27 21 26.1046 21 25V11C21 9.89543 20.1046 9 19 9Z" stroke="#6D6D6D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // Render a single lead card with Show button
  const renderLeadCard = (lead, isSponsored = false) => {
    const revealed = revealedLeads[lead.id];
    const isViewed = viewStatus.viewedIds?.includes(lead.id);
    const isLoading = showingLead === lead.id;

    return (
      <div key={lead.id} className={`lead-card ${isSponsored ? 'sponsored' : 'regular'}`}>
        <div className="lead-content">
          <div className="lead-card-body">
            {/* Name - always visible */}
            <div className="lead-checkbox-row">
              <div className="lead-company-info">
                {!isSponsored && <CheckboxIcon />}
                {isSponsored && (
                  <img
                    src="/uploads/builder-images/435ad5be8193012a0ce26a0fa29d39ebbf91e166_w112.png"
                    alt="Company Logo"
                    className="company-logo"
                  />
                )}
                <div className="company-details">
                  <h3 className="company-name">{getDisplayName(lead)}</h3>
                  <p className="company-meta">{lead.country} / {new Date(lead.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
              {isSponsored && <span className="sponsored-badge">Sponsored</span>}
            </div>

            {/* Hidden details - shown after "Show" click */}
            {revealed ? (
              <div className="lead-revealed-details">
                <div className="lead-detail-row">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#1F396C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 6L12 13L2 6" stroke="#1F396C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span className="lead-detail-value">{revealed.email || 'N/A'}</span>
                </div>
                <div className="lead-detail-row">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 16.92V19.92C22 20.48 21.56 20.93 21 20.97C20.44 21.01 19.88 21.01 19.33 20.97C10.07 20.14 2.86 12.93 2.03 3.67C1.99 3.12 2.44 2.67 3 2.67H6C6.55 2.67 7 3.12 7 3.67C7 5.02 7.2 6.33 7.57 7.56C7.7 8.01 7.56 8.5 7.21 8.85L5.39 10.67C7.07 13.97 10.03 16.93 13.33 18.61L15.15 16.79C15.5 16.44 15.99 16.3 16.44 16.43C17.67 16.8 18.98 17 20.33 17C20.88 17 21.33 17.45 21.33 18V18L22 16.92Z" stroke="#1F396C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span className="lead-detail-value">{revealed.phoneNumber || 'N/A'}</span>
                </div>
                <div className="lead-detail-row">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#1F396C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 2V8H20" stroke="#1F396C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span className="lead-detail-value">{revealed.details || lead.details || 'No requirement specified'}</span>
                </div>
              </div>
            ) : (
              <div className="lead-info">
                <p className="lead-keyword"><strong>Keyword:</strong> {lead.category}{lead.subCategory ? `, ${lead.subCategory}` : ''}</p>
                <p className="lead-category"><strong>Category:</strong> {lead.category}</p>
                <p className="lead-verified">Verified</p>
              </div>
            )}
          </div>

          {/* Show button */}
          {!revealed && (
            <button
              className={`show-lead-btn ${isLoading ? 'loading' : ''}`}
              onClick={() => handleShowLead(lead.id)}
              disabled={isLoading}
            >
              {isLoading ? (
                <span>Loading...</span>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/></svg>
                  <span>Show</span>
                </>
              )}
            </button>
          )}
          {revealed && (
            <span className="lead-viewed-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="#388E3C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Viewed
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="buy-leads-page">
      <Header />
      
      <main className="buy-leads-main">
        {/* Breadcrumb */}
        <div className="buy-leads-breadcrumb">
          <span className="breadcrumb-item">Home</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="breadcrumb-item">Buy Leads</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="breadcrumb-item">Building Materials</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="breadcrumb-item active">Cement</span>
        </div>

        <div className="buy-leads-content">
          {/* Main Content Area */}
          <div className="buy-leads-main-content">
            {/* Title and Description */}
            <div className="buy-leads-header">
              <h1 className="buy-leads-title">Buy Leads</h1>
              <p className="buy-leads-description">Find your desired category and explore products and services.</p>
            </div>

            {/* View Status Bar */}
            {user && viewStatus.viewsLimit > 0 && (
              <div className="buy-leads-view-status">
                <span className="view-status-text">
                  Buy Lead Views: <strong>{viewStatus.viewsUsed}</strong> / <strong>{viewStatus.viewsLimit}</strong> used
                </span>
                <div className="view-status-bar">
                  <div className="view-status-fill" style={{ width: `${Math.min(100, (viewStatus.viewsUsed / viewStatus.viewsLimit) * 100)}%` }}></div>
                </div>
              </div>
            )}

            {/* Search Filters */}
            <div className="buy-leads-filters" ref={filterRef}>
              <div className="filters-row">
                <div className="filter-inputs-group">
                  {/* Keyword Input */}
                  <div className="filter-input">
                    <input
                      type="text"
                      placeholder="Cement"
                      value={selectedKeyword}
                      onChange={(e) => setSelectedKeyword(e.target.value)}
                      className="filter-text-input"
                    />
                  </div>

                  {/* Country Dropdown */}
                  <div className="filter-dropdown" onClick={() => { setShowCountryDropdown(!showCountryDropdown); setShowCategoryDropdown(false); setShowDateDropdown(false); }}>
                    <span className={selectedCountry ? 'filter-selected' : 'filter-placeholder'}>{selectedCountry || 'Country'}</span>
                    <CaretDownIcon />
                    {showCountryDropdown && (
                      <div className="dropdown-menu">
                        <div className="dropdown-item" onClick={(e) => { e.stopPropagation(); setSelectedCountry(''); setShowCountryDropdown(false); }}>All Countries</div>
                        {countries.map((country) => (
                          <div key={country} className={`dropdown-item ${selectedCountry === country ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setSelectedCountry(country); setShowCountryDropdown(false); }}>
                            {country}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Category Dropdown */}
                  <div className="filter-dropdown" onClick={() => { setShowCategoryDropdown(!showCategoryDropdown); setShowCountryDropdown(false); setShowDateDropdown(false); }}>
                    <span className={selectedCategory ? 'filter-selected' : 'filter-placeholder'}>{selectedCategory || 'Category'}</span>
                    <CaretDownIcon />
                    {showCategoryDropdown && (
                      <div className="dropdown-menu">
                        <div className="dropdown-item" onClick={(e) => { e.stopPropagation(); setSelectedCategory(''); setShowCategoryDropdown(false); }}>All Categories</div>
                        {categories.map((category) => (
                          <div key={category} className={`dropdown-item ${selectedCategory === category ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setSelectedCategory(category); setShowCategoryDropdown(false); }}>
                            {category}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Date Dropdown */}
                  <div className="filter-dropdown" onClick={() => { setShowDateDropdown(!showDateDropdown); setShowCountryDropdown(false); setShowCategoryDropdown(false); }}>
                    <span className={selectedDate ? 'filter-selected' : 'filter-placeholder'}>{selectedDate || 'Date'}</span>
                    <CaretDownIcon />
                    {showDateDropdown && (
                      <div className="dropdown-menu">
                        {dateOptions.map((date) => (
                          <div key={date} className={`dropdown-item ${selectedDate === date ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setSelectedDate(date); setShowDateDropdown(false); }}>
                            {date}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Search Button */}
                <button className="filter-search-btn" onClick={handleSearch}>
                  <SearchIcon />
                  <span>Search</span>
                </button>
              </div>
            </div>

            {/* Sponsored Leads */}
            <div className="leads-section">
              {loading && <p style={{textAlign:'center',padding:'20px',color:'#666'}}>Loading buy leads...</p>}
              {!loading && sponsoredLeads.length === 0 && regularLeads.length === 0 && (
                <p style={{textAlign:'center',padding:'20px',color:'#666'}}>No buy leads found. Try different search criteria.</p>
              )}
              {sponsoredLeads.map((lead) => renderLeadCard(lead, true))}
            </div>

            {/* Regular Leads Section */}
            <div className="regular-leads-wrapper">
              <h2 className="regular-leads-title">Click "Show" to view contact details. {!user ? 'Sign up for a premium plan to access.' : ''}</h2>

              {/* Regular Leads */}
              <div className="leads-section regular">
                {regularLeads.map((lead) => renderLeadCard(lead, false))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="buy-leads-sidebar">
            {/* Guest Card */}
            <div className="sidebar-card guest-card">
              <div className="guest-info">
                <h4 className="guest-title">{user ? `Welcome, ${user.companyName || user.email?.split('@')[0]}` : 'Welcome. Guest'}</h4>
                <p className="guest-subtitle">{user ? `Plan: ${planDisplayName || 'Basic'}` : "You're not signed in"}</p>
              </div>
              <div className="premium-message">
                <p>Upgrade to view buyer contact details.</p>
                <ul className="plan-view-limits">
                  <li><strong>Basic:</strong> <span className="limit-cross">&#10007;</span> No access</li>
                  <li><strong>GCC:</strong> 50 views</li>
                  <li><strong>Global:</strong> 100 views</li>
                </ul>
              </div>
              <button className="upgrade-btn" onClick={() => navigate('/membership-packages')}>{user ? 'Upgrade your Plan' : 'Upgrade your Account'}</button>
            </div>

            {/* Ad Banners */}
            <div className="sidebar-ads">
              <img
                src="/uploads/builder-images/6a794d1fa5fc44a0b0ee4b374752f66c6cc8a75e_w570.png"
                alt="Advertisement"
                className="ad-banner"
              />
              <img
                src="/uploads/builder-images/85c0050b3eb2c65c275006565ddf4e75a4a838ad_w570.png"
                alt="Advertisement"
                className="ad-banner tall"
              />
            </div>

            {/* Recommended Content */}
            <div className="sidebar-card recommended-card">
              <h4 className="recommended-title">Recommended content</h4>
              <div className="recommended-items">
                <img
                  src="/uploads/builder-images/3758b271dc0adc00a0f825f9ffa9bd656c9b1b68_w506.png"
                  alt="Recommended"
                  className="recommended-item"
                />
                <img
                  src="/uploads/builder-images/3758b271dc0adc00a0f825f9ffa9bd656c9b1b68_w506.png"
                  alt="Recommended"
                  className="recommended-item"
                />
                <img
                  src="/uploads/builder-images/3758b271dc0adc00a0f825f9ffa9bd656c9b1b68_w506.png"
                  alt="Recommended"
                  className="recommended-item"
                />
              </div>
            </div>
          </aside>
        </div>

        {/* Pagination */}
        <Pagination currentPage={currentPage} totalPages={Math.max(1, Math.ceil(totalLeads / ITEMS_PER_PAGE))} onPageChange={setCurrentPage} />
      </main>

      <Footer />
    </div>
  );
};

export default BuyLeadsPage;
